import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CanvasWindowMaximizeIcon } from './icons'
import { getCanvasNoteColorHex } from './utils/canvasNoteStyle'
import { getMemoColor } from './utils/canvasMemoStyle'
import { getCanvasNoteFont } from './utils/canvasFonts'
import {
  getCanvasNoteShadowUrlFor,
  CANVAS_NOTE_SHADOW_MARGIN as SHADOW_MARGIN,
} from './utils/canvasNoteShadow'
import { peekStickyFontSize, peekStickyLineHeight } from './utils/canvasPeek'
import { getCanvasTape, readCanvasTape } from './tapes/canvasTapes'
import type { CanvasNote } from '@/src/renderer/lib/db/models'
import type { CanvasPeekProps } from './CanvasPeek.types'
import './CanvasPeek.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/**
 * Eine Miniatur eines **Canvas-Elements** im Anriss — statisch, ohne React
 * Flow, ohne Bearbeiten. Sie trägt Fläche, Art und Text; alles andere
 * (Eselsohr, Autor, Ziehpunkte) hat im Streifen nichts zu sagen.
 *
 * Die Fläche kommt als `--note-fill`, derselbe Name wie am echten Zettel —
 * wer später eine Regel vom Zettel übernimmt, findet die Variable vor.
 */
/**
 * Der **Klebestreifen** einer Miniatur — dieselbe Zeichnung, dieselbe Lage,
 * derselbe Winkel wie auf dem Canvas, nur im Maßstab des Streifens.
 *
 * **Statisch.** Auf dem Canvas ist der Streifen der GRIFF eines geklebten
 * Zettels: Er nimmt Klicks an, lässt sich drehen, rollt beim Ankleben auf und
 * beim Ablösen ab (`CanvasTape`). Im Anriss ist er das, was er dort auch ist —
 * ein Stück Papier über der Ecke. Kein Knopf, keine Rolle, keine Bewegung.
 *
 * Er gehört dazu, weil er die Frage beantwortet, die der Anriss stellt: „was
 * liegt da?" Ein geklebter Zettel sieht anders aus als ein loser, und wer im
 * Streifen einen losen sah und beim Öffnen einen geklebten fand, hat zweimal
 * hingesehen (gewünscht 2026-09-11).
 *
 * Die Maße kommen aus der Zeichnung, die Lage aus der Zeile — beides wie am
 * echten Streifen. Den Maßstab legt die SCSS an, an EINER Stelle.
 */
const CanvasPeekTape: React.FC<{ tape: string }> = ({ tape }) => {
  const look = readCanvasTape(tape)
  const shape = getCanvasTape(look)
  if (!look || !shape) return null

  return (
    <span
      className={`canvas-peek__tape canvas-peek__tape--${look.spot}`}
      aria-hidden="true"
      style={{
        '--tape-image': `url("${shape.url}")`,
        '--tape-width': `${shape.width}px`,
        '--tape-height': `${shape.height}px`,
        '--tape-angle': `${look.angle}deg`,
      } as React.CSSProperties}
    />
  )
}

const CanvasPeekNote: React.FC<{ note: CanvasNote; slot: number }> = ({ note, slot }) => {
  /*
    Der **Notiz** trägt dieselbe gerechnete Schatten-Textur wie auf dem
    Canvas — ein `box-shadow` kann sie nicht nachbilden (siehe
    `canvasNoteShadow.ts`), und ohne sie sähe der Zettel im Anriss anders aus
    als der, den man beim Öffnen vorfindet.

    Immer die Variante `left`: Dorthin ragen die Zettel aus dem Streifen
    heraus, und der Schatten hebt genau diese Ecke an. Auf dem Canvas würfelt
    die Kennung eine der drei aus, damit eine Wand voller Notizen unregelmäßig
    wirkt — hier liegen zwei, und zwei verschiedene Neigungen läsen sich als
    Fehler, nicht als Zufall.

    Die **Notiz** bekommt sie nicht: Die Textur ist auf das Quadrat des
    Notiz zugeschnitten und würde an einem mitwachsenden Zettel in die
    Länge gezogen. Sie behält ihren `box-shadow` (CanvasPeek.scss).
  */
  const shadow = note.kind === 'note' ? getCanvasNoteShadowUrlFor('left') : ''

  /*
    Und dieselbe **Schrift** wie auf dem Canvas — über dieselben vier Variablen,
    die `NoteNode` und `MemoNode` setzen. Wer seinen Canvas in einer Handschrift
    führt, sah im Streifen bis dahin die App-Schrift und beim Öffnen etwas
    anderes, als der Streifen versprochen hatte.

    Der **optische Ausgleich** (`scale`) muss mit: Zwei Schriften in derselben
    `font-size` sind nicht gleich groß zu SEHEN — Caveat bringt 0,373 em x-Höhe,
    Permanent Marker 0,623. Ohne ihn schwankten die Miniaturen sichtbar.

    Kein Abonnement auf `subscribeCanvasFontReady`: Das braucht der Canvas, weil
    er die Zettelgröße MISST. Hier wird nichts gemessen — der Browser zeichnet
    von selbst neu, sobald die Datei da ist.
  */
  const font = getCanvasNoteFont(note.font)

  /*
    **Und dieselbe GRÖSSE wie auf dem Canvas** — für die **Notiz**, die sie sich
    aus ihrem Text errechnet (`peekStickyFontSize`). Hier stand eine feste
    Größe, und damit brach der Streifen sein Versprechen: „Hello" steht auf dem
    Canvas groß da und lag im Anriss als Krümel (gemeldet 2026-09-11).

    Die **Memo** behält ihre feste Größe: Sie rechnet auch auf dem Canvas nichts
    aus (MemoNode.scss: 15px), dort wächst statt der Schrift der Zettel.

    Gecacht, weil die Suche in `computeStickyFontSize` den Umbruch wirklich
    durchspielt — der Streifen rendert bei jeder Bewegung des Panels mit.
  */
  const sticky = useMemo(
    () => (note.kind === 'note'
      ? { size: peekStickyFontSize(note), lineHeight: peekStickyLineHeight(note) }
      : null),
    [note],
  )

  return (
  <div
    className={`canvas-peek__note canvas-peek__note--${note.kind} canvas-peek__note--slot-${slot}`}
    style={{
      '--note-fill': getCanvasNoteColorHex(note.color),
      /*
        Der **Streifen** des Memos — auch im Anriss, sonst sähe die Miniatur
        aus wie ein anderes Element.

        Die Töne kommen aus der MEMO-Palette, die Fläche darunter aber aus
        der Notiz-Palette (`--note-fill`, oben): Die Miniatur war schon immer
        eine Annäherung, kein Abbild, und ihre Fläche bewusst satter. Beide
        liegen in derselben Farbfamilie, im Maßstab des Streifens trägt das.
      */
      ...(note.kind === 'memo' ? {
        '--note-stripe': getMemoColor(note.color).stripe,
        '--note-stripe-line': getMemoColor(note.color).stripeLine,
      } : {}),
      '--note-font': font.stack,
      '--note-font-weight': font.weight,
      '--note-title-weight': font.titleWeight,
      '--note-font-scale': font.scale,
      ...(sticky ? {
        '--note-font-size': `${sticky.size}px`,
        '--note-line-height': sticky.lineHeight,
      } : {}),
      // Die Ränder müssen mit: Sie sagen, wie weit die Textur über den Zettel
      // hinausragt. Getippte Kopien im SCSS liefen irgendwann auseinander, und
      // dann wäre die Textur gestaucht und ihr äußerer Rand abgeschnitten.
      ...(shadow ? {
        '--note-shadow': `url(${shadow})`,
        '--note-shadow-l': SHADOW_MARGIN.l,
        '--note-shadow-r': SHADOW_MARGIN.r,
        '--note-shadow-t': SHADOW_MARGIN.t,
        '--note-shadow-b': SHADOW_MARGIN.b,
      } : {}),
    } as React.CSSProperties}
  >
    {note.tape && <CanvasPeekTape tape={note.tape} />}
    {note.kind === 'memo' && note.title !== null && note.title !== '' && (
      <div className="canvas-peek__note-title">{note.title}</div>
    )}
    <div className="canvas-peek__note-text">{note.content}</div>
  </div>
  )
}

/**
 * Der **Canvas-Anriss** — was der geschlossene Projekt-Canvas zeigt, sobald
 * Elemente darauf liegen (CONTEXT.md, „Canvas-Anriss"; Figma 4869:60140).
 *
 * Ein 77px schmaler Streifen mit Punktraster und Rahmen, in den die zuletzt
 * bearbeiteten Elemente von links oben hineinragen. **Dekorativ, kein
 * Ausschnitt**: Die Zettel liegen an festen Plätzen und sagen „hier liegt
 * etwas" — sie zeigen nicht, was am Rand des gemerkten Ausschnitts läge.
 *
 * Drei Klickziele, drei Ergebnisse:
 * - die Fläche → **geteilt**
 * - der Knopf oben → **maximiert** (dasselbe Icon, dieselbe Wirkung wie im
 *   geöffneten Canvas)
 * - die Werkzeug-Pille (als `children`) → wie bisher: stellt scharf und öffnet
 *   geteilt
 *
 * Die Fläche ist ein eigener Knopf UNTER allem anderen, kein Handler am
 * Wurzelelement: So erreichen Klicks auf Pille und Maximieren-Knopf sie gar
 * nicht erst, und niemand muss `stopPropagation` in Erinnerung behalten.
 *
 * **Die Zettel nehmen Zeiger an** — sie sind ein zweites Zeigerziel für dieselbe
 * Sache. Über dem Streifen liegt die Fläche ohnehin darunter; über dem Stück,
 * das links herausragt, liegt sie nicht mehr, und dort fiel bis dahin sowohl der
 * Hover als auch der Klick durch den Zettel hindurch — in die **Aufgabenliste**.
 * Wer den Zettel sah und ihn anklickte, traf eine Aufgabe.
 *
 * Der Klick geht deshalb an den ZettelBEREICH und nicht an den Zettel: Er
 * blubbert von dort hoch, und eine Stelle genügt. Zeiger nimmt trotzdem nur der
 * Zettel an (CanvasPeek.scss) — der Bereich ist per `clip-path` weit nach links
 * offen und schluckte sonst jeden Klick über der halben Liste.
 *
 * Ein Knopf wird der Zettel dadurch nicht: `__surface` bleibt der Weg für
 * Tastatur und Screenreader, er trägt den Namen. Der Zettel ist die Fläche, die
 * man sieht.
 *
 * **In Bewegung nur an der Geste** (CONTEXT.md): `entering` lässt Zettel und
 * Knopf beim Zuziehen einfahren (die Pille folgt, sobald sie Kind wird) und
 * sperrt solange den Hover; `ghost` macht aus dem Anriss die Schicht ohne
 * Fläche über einer wachsenden oder schrumpfenden Canvas-Fläche, `leaving`
 * lässt sie ausblenden. Alles entscheidet das Panel — steht der Anriss einfach
 * da (Projektwechsel, Start), trägt er nichts davon.
 *
 * @example
 * <CanvasPeek notes={pickPeekNotes(notes)} onOpenSplit={…} onOpenMaximized={…}>
 *   {toolbarPlate}
 * </CanvasPeek>
 */
export const CanvasPeek: React.FC<CanvasPeekProps> = ({
  notes, onOpenSplit, onOpenMaximized, children, className, entering = false, ghost = false, leaving = false,
}) => {
  const { t } = useTranslation()

  const rootClass = [
    'canvas-peek',
    entering && 'canvas-peek--entering',
    ghost && 'canvas-peek--ghost',
    leaving && 'canvas-peek--leaving',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div className={rootClass}>
      {/*
        Die Schicht beim Öffnen hat keinen Fläche-Knopf: Sie nimmt ohnehin
        keine Zeiger an, und ein zweiter „Öffnen"-Knopf im Baum wäre für
        Screenreader und Tests ein Doppelgänger des Canvas, der schon offen ist.
      */}
      {!ghost && (
        // `⇧C` holt den Canvas geteilt herein (`useCanvasPanelShortcut`) — genau das tut die Fläche.
        <WithTooltip content={t('projectCanvas.peek.open')} shortcut={{ kind: 'character', text: 'C', shift: true }}>
          <button
            type="button"
            className="canvas-peek__surface"
            aria-label={t('projectCanvas.peek.open')}
            onClick={onOpenSplit}
            data-intro-anchor="board"
          />
        </WithTooltip>
      )}
      {/*
        Zwei Kästen, und beide werden gebraucht: `__notes` ist der RAUM unter
        der Pille (und klemmt, was aus ihm herausfällt), `__notes-stack` der
        STAPEL darin. Die Zettel liegen absolut übereinander, haben also keine
        eigene Höhe — der Stapel trägt sie, damit die Zentrierung im Raum
        etwas zu messen hat.
      */}
      <div
        className="canvas-peek__notes"
        aria-hidden="true"
        onClick={ghost ? undefined : onOpenSplit}
      >
        <div className="canvas-peek__notes-stack">
          {notes.map((note, index) => (
            <CanvasPeekNote key={note.id} note={note} slot={index} />
          ))}
        </div>
      </div>
      {/* `C` holt den Canvas maximiert herein (`useCanvasPanelShortcut`). */}
      <WithTooltip content={t('projectCanvas.toolbar.maximize')} shortcut={{ kind: 'character', text: 'C' }}>
        <button
          type="button"
          className="canvas-peek__maximize"
          aria-label={t('projectCanvas.toolbar.maximize')}
          onClick={onOpenMaximized}
        >
          <CanvasWindowMaximizeIcon size={20} />
        </button>
      </WithTooltip>
      {children}
    </div>
  )
}
